import React from 'react';
import {
  MdLogin,
  MdPersonAdd,
  MdPersonOutline,
  MdConfirmationNumber,
} from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';

interface TicketCheckoutEntryProps {
  onContinueAsGuest: () => void;
}

export const TicketCheckoutEntry: React.FC<TicketCheckoutEntryProps> = ({
  onContinueAsGuest,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.pathname || '/tickets';

  const handleSignIn = () => {
    navigate('/sign-in', { state: { returnTo } });
  };

  const handleSignUp = () => {
    navigate('/sign-up', { state: { returnTo } });
  };

  const options = [
    {
      id: 'sign-in',
      title: 'Sign In',
      description:
        'Already have an account? Sign in to manage bookings and apply season passes.',
      icon: MdLogin,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      buttonLabel: 'Sign In',
      buttonClass:
        'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
      onClick: handleSignIn,
      accentBorder: 'hover:border-blue-400 hover:shadow-md',
    },
    {
      id: 'create-account',
      title: 'Create an Account',
      description:
        'New here? Register to save your bookings and access them anytime.',
      icon: MdPersonAdd,
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      buttonLabel: 'Create Account',
      buttonClass: 'bg-sky-600 text-white hover:bg-sky-700 focus:ring-sky-500',
      onClick: handleSignUp,
      accentBorder: 'hover:border-sky-400 hover:shadow-md',
    },
    {
      id: 'guest',
      title: 'Continue as Guest',
      description:
        'Checkout quickly without an account. You will still get an email confirmation.',
      icon: MdConfirmationNumber,
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600',
      buttonLabel: 'Continue as Guest',
      buttonClass:
        'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 focus:ring-slate-400',
      onClick: onContinueAsGuest,
      accentBorder: 'hover:border-slate-400 hover:shadow-md',
    },
  ];

  return (
    <div className="max-w-5xl mx-auto mt-2 md:mt-4">
      <div className="text-center mb-8 px-2">
        <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-100">
          <MdPersonOutline className="w-7 h-7 text-blue-600" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          How would you like to check out?
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Choose an option below to continue with your ticket purchase.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {options.map(option => {
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              type="button"
              onClick={option.onClick}
              className={`group flex flex-col items-center text-center bg-white rounded-xl border-2 border-gray-200 p-6 md:p-7 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${option.accentBorder}`}
            >
              <div
                className={`w-16 h-16 ${option.iconBg} rounded-full flex items-center justify-center mb-5 transition-transform duration-200 group-hover:scale-105`}
              >
                <Icon className={`w-8 h-8 ${option.iconColor}`} />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {option.title}
              </h3>

              <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                {option.description}
              </p>

              <span
                className={`w-full inline-flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${option.buttonClass}`}
              >
                {option.buttonLabel}
              </span>
            </button>
          );
        })}
      </div>

      <p className="mt-6 text-center text-sm text-gray-500 px-4">
        Guest checkout still sends a confirmation email. An account is required
        later if you want to apply a season pass.
      </p>
    </div>
  );
};
